import axios from 'axios';

import { getBackendBaseUrl } from '../config/apiBaseUrl';

const API_URL = getBackendBaseUrl();
export const AUTH_UNAUTHORIZED_EVENT = 'elios:auth-unauthorized';
export const AUTH_TOKEN_STORAGE_KEY = 'elios_auth_token';
axios.defaults.withCredentials = true;

export const getAuthToken = () => {
  try {
    return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

export const setAuthToken = (token) => {
  if (!token) {
    return;
  }
  try {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch (error) {
    // noop
  }
};

export const clearAuthToken = () => {
  try {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  } catch (error) {
    // noop
  }
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password })
};

// Questions API
export const questionsAPI = {
  getAll: () => api.get('/questions'),
  getAllAdmin: () => api.get('/admin/questions'),
  create: (data) => api.post('/admin/questions', data),
  update: (id, data) => api.put(`/admin/questions/${id}`, data),
  delete: (id) => api.delete(`/admin/questions/${id}`)
};

// Form API
export const formAPI = {
  submit: (data) => api.post('/form/submit', data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  getResponses: (userId) => api.get('/form/responses', { params: userId ? { user_id: userId } : {} }),
  updateResponse: (id, data) => api.put(`/form/responses/${id}`, data)
};

// Goals API
export const goalsAPI = {
  getAll: (params = {}) => api.get('/goals', { params }),
  getByPillar: (pillar, params = {}) => api.get(`/goals/pillar/${pillar}`, { params }),
  create: (data, params = {}) => api.post('/goals', data, { params }),
  update: (id, data, params = {}) => api.put(`/goals/${id}`, data, { params }),
  delete: (id, params = {}) => api.delete(`/goals/${id}`, { params }),
  getHistory: (id, params = {}) => api.get(`/goals/${id}/history`, { params }),
  getWeeklyProgress: (params = {}) => api.get('/goals/weekly-progress', { params })
};

// AI API
export const aiAPI = {
  analyze: (data) => api.post('/ai/analyze', data),
  chat: (data) => api.post('/ai/chat', data),
  getChatHistory: () => api.get('/ai/chat/history'),
  clearChatHistory: () => api.delete('/ai/chat/history')
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  getUsersFormResponses: (params = {}) => api.get('/admin/users/form-responses', { params }),
  updateUserGoal: (userId, goalId, data) => api.put(`/admin/users/${userId}/goals/${goalId}`, data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  uploadUserPhoto: (id, formData) => api.post(`/admin/users/${id}/profile-photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getAIKnowledge: () => api.get('/admin/ai/knowledge'),
  addAIKnowledge: (data) => api.post('/admin/ai/knowledge', data),
  deleteAIKnowledge: (id) => api.delete(`/admin/ai/knowledge/${id}`),
  getMetadata: () => api.get('/admin/metadata'),
  updateMetadata: (data) => api.put('/admin/metadata', data),
  getWeeklyProgressMonitor: () => api.get('/admin/weekly-progress-monitor'),
  getWeeklyProgressCycle: (userId) => api.get(`/admin/weekly-progress-cycle/${userId}`),
  getObservabilityLogs: (params = {}) => api.get('/admin/observability/logs', { params }),
  getMagnusProgress: () => api.get('/admin/magnus-progress'),
  getMagnusDocument: (userId) => api.get(`/admin/magnus/document/${userId}`),
  updateMagnusDocument: (userId, content) => api.put(`/admin/magnus/document/${userId}`, { content })
};

// Gamification API
export const gamificationAPI = {
  getMonthlyRanking: (month) => api.get('/admin/gamification/ranking', { params: month ? { month } : {} }),
  getAllTimeRanking: () => api.get('/admin/gamification/ranking/all-time'),
  downloadClosingPdf: (month) => api.get('/admin/gamification/report/closing.pdf', { params: month ? { month } : {}, responseType: 'blob' }),
  getMemberRanking: (month) => api.get('/gamification/ranking', { params: month ? { month } : {} }),
  getUserAchievements: (userId, month) => api.get(`/gamification/achievements/${userId}`, { params: month ? { month } : {} }),
  approveAchievement: (recordId, reason) => api.post(`/admin/gamification/achievements/${recordId}/approve`, { reason }),
  rejectAchievement: (recordId, reason) => api.post(`/admin/gamification/achievements/${recordId}/reject`, { reason }),
  getGameRanking: ({ start, end, name, page = 1, pageSize = 30 } = {}) =>
    api.get('/admin/gamification/game-ranking', { params: { start, end, name, page, page_size: pageSize } }),
  getAudit: ({ start, end, name, status, page = 1, pageSize = 30 } = {}) =>
    api.get('/admin/gamification/audit', { params: { start, end, name, status, page, page_size: pageSize } }),
};

// Event points API (admin only)
export const eventsAPI = {
  listEvents: ({ activeOnly = false } = {}) =>
    api.get('/admin/events', { params: { active_only: activeOnly } }),
  createEvent: (data) => api.post('/admin/events', data),
  updateEvent: (eventId, data) => api.put(`/admin/events/${eventId}`, data),
  deleteEvent: (eventId) => api.delete(`/admin/events/${eventId}`),
  awardPoints: (eventId, { entries, award_date, note } = {}) =>
    api.post(`/admin/events/${eventId}/awards`, { entries, award_date, note }),
  listEventAwards: (eventId, { status, page = 1, pageSize = 200 } = {}) =>
    api.get(`/admin/events/${eventId}/awards`, { params: { status, page, page_size: pageSize } }),
  listAwardsLog: ({ start, end, status, page = 1, pageSize = 50 } = {}) =>
    api.get('/admin/events/awards', { params: { start, end, status, page, page_size: pageSize } }),
  updateAwardDate: (awardId, award_date) =>
    api.patch(`/admin/events/awards/${awardId}`, { award_date }),
  revokeAward: (awardId, reason) =>
    api.post(`/admin/events/awards/${awardId}/revoke`, { reason }),
};

// Dashboard API
export const dashboardAPI = {
  getPoints: (params) => api.get('/dashboard/points', { params }),
  getStats: (userId) => api.get('/dashboard/stats', { params: userId ? { user_id: userId } : {} })
};

// Init API (run once)
export const initAPI = {
  initQuestions: () => api.post('/init/questions'),
  initAdmin: () => api.post('/init/admin')
};

export default api;

// Independent client: TV displays do not require a session or authentication headers.
const publicRankingClient = axios.create({ baseURL: `${API_URL}/api/public/gamification`, withCredentials: false });
export const publicGamificationAPI = {
  getMonthlyRanking: (month) => publicRankingClient.get('/ranking', { params: month ? { month } : {} }),
  getAllTimeRanking: () => publicRankingClient.get('/ranking/all-time'),
};
